import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import Transition, { TransitionStatus } from "react-transition-group/Transition";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    scrollFixed?: MobiusPageProps;
    container?: InjectableCss;
    secondaryContainer?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    mainContainer?: InjectableCss;
    navigationContainer?: InjectableCss;
}
export interface StickyHeaderStyles {
    navigationContainer: InjectableCss;
    mainContainer?: InjectableCss;
    container?: InjectableCss;
}
export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, true, true, true],
        css: css`
            position: fixed;
            z-index: 999;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            overflow: initial !important;
                        `,
                        css`
                            overflow: initial !important;
                        `,
                        css`
                            height: auto;
                        `,
                        css`
                            max-width: 100% !important;
                        `,
                        null,
                    ],
                    "max",
                )}
            ~ main[class^="PageStyle"] {
                padding: 15px 2.5rem;
                min-height: 550px;
                position: relative;
                top: 128px;
                z-index: 99;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 15px 1.5rem;
                            position: relative;
                            top: inherit;
                        `,
                        css`
                            padding: 15px 1.5rem;
                            position: relative;
                            top: inherit;
                        `,
                        css`
                            position: relative;
                            top: 124px;
                        `,
                        null,
                        null,
                    ])}
            }
            ~ main[data-test-selector^="homePage"] {
                padding: 0px !important;
                z-index: 99;
            }
        `,
    },
    mainContainer: {
        css: css`
            width: 100%;
            z-index: 10;
            position: relative;
            > div[class^="Wrapper"] {
                background: ${getColor("common.background")};
            }
        `,
    },
    navigationContainer: {
        css: css`
            background-color: ${getColor("primary")};
            width: 100%;
            max-width: 100%;
            margin: 0 auto;
        `,
    },
    container: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
            ~ header[class^="PageStyle"] {
                & + div[class^="PageStyle"] {
                    nav[data-test-selector="pageBreadcrumbs"] {
                        span {
                            text-transform: capitalize;
                        }
                        span[aria-current="page"] {
                            width: 300px;
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                        }

                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    display: none;
                                `,
                                css`
                                    display: none;
                                `,
                                css`
                                    white-space: nowrap;
                                    overflow: hidden;
                                    overflow-x: auto;
                                    padding-bottom: 12px;
                                `,
                                css`
                                    white-space: nowrap;
                                    overflow: hidden;
                                    overflow-x: auto;
                                    padding-bottom: 12px;
                                `,
                                null,
                            ])}
                    }
                }
            }
        `,
    },
};

export const stickyHeaderStyles: StickyHeaderStyles = {
    navigationContainer: {
        css: css`
            flex: 1;
            max-width: 100%;
            margin: 0 auto;
            background-color: ${getColor("primary")};
            position: relative;
            justify-content: space-between;
        `,
    },
    container: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
};

const HeaderWrapper = styled(StyledWrapper)<{ transitionState: TransitionStatus }>`
    transition: top ease-in-out 350ms;
    ${({ transitionState }) => {
        switch (transitionState) {
            case "entering":
                return "top:-100px;";
            case "entered":
                return "position:fixed;top:0;";
            case "exiting":
            case "exited":
            default:
                return "";
        }
    }}
`;
let styles = headerStyles;
let sizeObserverTimeoutId: any;

const Header = ({ id, siteName, session, toaster, history, myAccountPageUrl, dismissRfqUpdates }: Props) => {
    const [scrollOffset, setScrollOffset] = useState(0);
    const [isSticky, setIsSticky] = useState(false);
    const [headerHeight, setHeaderHeight] = useState(0);
    const stickyAnchor = useRef<HTMLDivElement>(null);

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    const toggleSticky = useCallback(() => {
        if (stickyAnchor!.current!.getBoundingClientRect().bottom <= 0 && document.documentElement.clientWidth > 990) {
            setIsSticky(true);
            styles = mergeToNew(headerStyles, stickyHeaderStyles);
        } else {
            setIsSticky(false);
            styles = mergeToNew(headerStyles, {});
        }
    }, [scrollOffset]);

    const handleResize = useCallback(() => {
        toggleSticky();
    }, [toggleSticky]);

    const addStyleRel = useCallback(() => {
        const updateRelAttr = document.querySelector('link[type="text/css"]');
        updateRelAttr?.setAttribute("rel", "preload stylesheet");
    }, []);

    useEffect(() => {
        handleResize();
        // To Load External Fonts
        addStyleRel();
        window.addEventListener("scroll", toggleSticky, { passive: true });
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("scroll", toggleSticky);
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    const headerRef = React.createRef<HTMLDivElement>();

    useEffect(() => {
        if (headerRef.current && (window as any).ResizeObserver) {
            const resizeObserver = new (window as any).ResizeObserver((entries: any) => {
                clearTimeout(sizeObserverTimeoutId);
                sizeObserverTimeoutId = setTimeout(() => {
                    if (entries[0] && entries[0].target) {
                        setHeaderHeight(entries[0].target.clientHeight);
                    }
                }, 50);
            });

            resizeObserver.observe(headerRef.current);
            return () => {
                resizeObserver.disconnect();
            };
        }
    }, [headerRef.current]);

    return (
        <>
            {/* {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>} */}
            <Page as="header" isHeader={true} {...styles.page}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <StyledWrapper ref={stickyAnchor}>
                    <EagerLoadingLogoProvider value={true}>
                        <Transition in={isSticky} timeout={{ enter: 130, exit: 130 }}>
                            {state => (
                                <StyledWrapper
                                    style={{
                                        height: headerHeight > 0 ? headerHeight : "auto",
                                        backgroundColor: "inherit",
                                    }}
                                    id="header-wrapper"
                                >
                                    <HeaderWrapper {...styles.mainContainer} transitionState={state} ref={headerRef}>
                                        <Zone contentId={id} zoneName="SecondaryNavigation" fixed />
                                        <StyledWrapper {...styles.navigationContainer} id="MenuBAr">
                                            <StyledWrapper {...styles.container}>
                                                <Zone contentId={id} zoneName="MainNavigation" fixed />
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </HeaderWrapper>
                                </StyledWrapper>
                            )}
                        </Transition>
                        <Zone contentId={id} zoneName="Breadcrumb" />
                    </EagerLoadingLogoProvider>
                    <span ref={afterHeader} tabIndex={-1} />
                </StyledWrapper>
            </Page>
            {/* <Page as="header" isHeader={true} {...styles.page}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <StyledWrapper ref={stickyAnchor}>
                    <EagerLoadingLogoProvider value={true}>
                        <Transition in={isSticky} timeout={{ enter: 130, exit: 130 }}>
                            {state => (
                                <StyledWrapper
                                    style={{
                                        height: headerHeight > 0 ? headerHeight : "auto",
                                        backgroundColor: "inherit",
                                    }}
                                    id="header-wrapper"
                                >
                                    <HeaderWrapper {...styles.mainContainer} transitionState={state} ref={headerRef}>
                                        <Zone contentId={id} zoneName="SecondaryNavigation" />
                                        <StyledWrapper {...styles.navigationContainer} id="MenuBAr">
                                            <StyledWrapper {...styles.container}>
                                                <Zone contentId={id} zoneName="MainNavigation" />
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </HeaderWrapper>
                                </StyledWrapper>
                            )}
                        </Transition>
                        <Zone contentId={id} zoneName="Breadcrumb" />
                    </EagerLoadingLogoProvider>
                    <span ref={afterHeader} tabIndex={-1} />
                </StyledWrapper>
            </Page> */}
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
